import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:8081";

const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/* On a 401, try the refresh token once, then replay the original request. */
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      const refreshToken = localStorage.getItem("refreshToken");
      if (refreshToken) {
        try {
          const { data } = await axios.post(
            `${API_BASE}/api/crm/auth/refresh-token`,
            { refreshToken },
          );
          localStorage.setItem("accessToken", data.accessToken);
          localStorage.setItem("refreshToken", data.refreshToken);
          original.headers.Authorization = `Bearer ${data.accessToken}`;
          return api(original);
        } catch {
          localStorage.clear();
          window.location.href = "/signin";
        }
      }
    }
    return Promise.reject(err);
  },
);

/* Auth */
export const signup = (body) => api.post("/api/crm/auth/signup", body);
export const login = (email, password) =>
  api.post("/api/crm/auth/login", { email, password });
export const verifyOtp = (pendingToken, code) =>
  api.post("/api/crm/auth/verify-otp", { pendingToken, code });
export const resendOtp = (pendingToken) =>
  api.post("/api/crm/auth/resend-otp", { pendingToken });
export const refreshTokenApi = (refreshToken) =>
  api.post("/api/crm/auth/refresh-token", { refreshToken });
export const logoutApi = () => api.post("/api/crm/auth/logout");
export const getMe = () => api.get("/api/crm/auth/me");
export const updateMe = (body) => api.put("/api/crm/auth/me", body);
export const changePassword = (body) => api.put("/api/crm/auth/password", body);

/* Organization */
export const getOrg = () => api.get("/api/crm/org");
export const updateOrg = (body) => api.put("/api/crm/org", body);
export const getPermissionCatalog = () =>
  api.get("/api/crm/org/permission-catalog");
export const listOrgRoles = () => api.get("/api/crm/org/roles");
export const createOrgRole = (body) => api.post("/api/crm/org/roles", body);
export const getOrgRole = (id) => api.get(`/api/crm/org/roles/${id}`);
export const updateOrgRole = (id, body) =>
  api.put(`/api/crm/org/roles/${id}`, body);
export const deleteOrgRole = (id) => api.delete(`/api/crm/org/roles/${id}`);
export const inviteOrgUser = (body) =>
  api.post("/api/crm/org/users/invite", body);

/* Users (org-role based — creation happens only via the invite endpoint) */
export const listUsers = (params) => api.get("/api/crm/users", { params });
export const getAssignableUsers = () => api.get("/api/crm/users/assignable");
export const updateUser = (id, body) => api.put(`/api/crm/users/${id}`, body);
export const setUserStatus = (id, isActive) =>
  api.put(`/api/crm/users/${id}/status`, { isActive });
export const resetUserPassword = (id, newPassword) =>
  api.post(`/api/crm/users/${id}/reset-password`, { newPassword });

/* Meta — objects & fields */
export const listMetaObjects = () => api.get("/api/crm/meta/objects");
export const createMetaObject = (body) => api.post("/api/crm/meta/objects", body);
export const updateMetaObject = (id, body) =>
  api.put(`/api/crm/meta/objects/${id}`, body);
export const deleteMetaObject = (id) =>
  api.delete(`/api/crm/meta/objects/${id}`);
export const createMetaField = (objectId, body) =>
  api.post(`/api/crm/meta/objects/${objectId}/fields`, body);
export const updateMetaField = (id, body) =>
  api.put(`/api/crm/meta/fields/${id}`, body);
export const deleteMetaField = (id) => api.delete(`/api/crm/meta/fields/${id}`);
export const applySalesCrmTemplate = () =>
  api.post("/api/crm/meta/templates/sales-crm");

/* Meta — pipelines */
export const listPipelines = (objectId) =>
  api.get(`/api/crm/meta/objects/${objectId}/pipelines`);
export const createPipeline = (objectId, body) =>
  api.post(`/api/crm/meta/objects/${objectId}/pipelines`, body);
export const savePipeline = (id, body) =>
  api.put(`/api/crm/meta/pipelines/${id}`, body);
export const deletePipeline = (id) =>
  api.delete(`/api/crm/meta/pipelines/${id}`);

/* Records — generic engine */
export const listRecords = (slug, params) =>
  api.get(`/api/crm/o/${slug}/records`, { params });
export const createRecord = (slug, body) =>
  api.post(`/api/crm/o/${slug}/records`, body);
export const getRecord = (slug, id) =>
  api.get(`/api/crm/o/${slug}/records/${id}`);
export const updateRecord = (slug, id, body) =>
  api.put(`/api/crm/o/${slug}/records/${id}`, body);
export const deleteRecord = (slug, id) =>
  api.delete(`/api/crm/o/${slug}/records/${id}`);
export const transitionRecord = (slug, id, toStageId) =>
  api.post(`/api/crm/o/${slug}/records/${id}/transition`, { toStageId });
/* The moves this caller may make on a record right now — each option carries
   `allowed` and, when blocked, a human `reason`. */
export const getRecordTransitions = (slug, id) =>
  api.get(`/api/crm/o/${slug}/records/${id}/transitions`);
export const getObjectBoard = (slug, params) =>
  api.get(`/api/crm/o/${slug}/board`, { params });
/* Records in other objects that point at this one through a relation field —
   grouped by the object and the field that links them. */
export const getRelatedRecords = (slug, id) =>
  api.get(`/api/crm/o/${slug}/records/${id}/related`);

/* Workflows */
export const listWorkflows = () => api.get("/api/crm/workflows");
export const createWorkflow = (body) => api.post("/api/crm/workflows", body);
export const updateWorkflow = (id, body) =>
  api.put(`/api/crm/workflows/${id}`, body);
export const deleteWorkflow = (id) => api.delete(`/api/crm/workflows/${id}`);
export const toggleWorkflow = (id) =>
  api.post(`/api/crm/workflows/${id}/toggle`);
export const listWorkflowRuns = (id) =>
  api.get(`/api/crm/workflows/${id}/runs`);

/* Dashboard */
export const getDashboardSummary = () => api.get("/api/crm/dashboard/summary");

/* Platform — API keys */
export const listApiKeys = () => api.get("/api/crm/platform/api-keys");
export const createApiKey = (body) =>
  api.post("/api/crm/platform/api-keys", body);
export const revokeApiKey = (id) =>
  api.delete(`/api/crm/platform/api-keys/${id}`);

/* Platform — generated API documentation */
export const getApiDocs = () => api.get("/api/crm/platform/api-docs");
export const getOpenApiSpec = () => api.get("/api/crm/platform/openapi.json");

/* Platform — webhooks */
export const listWebhooks = () => api.get("/api/crm/platform/webhooks");
export const createWebhook = (body) =>
  api.post("/api/crm/platform/webhooks", body);
export const updateWebhook = (id, body) =>
  api.put(`/api/crm/platform/webhooks/${id}`, body);
export const deleteWebhook = (id) =>
  api.delete(`/api/crm/platform/webhooks/${id}`);
export const listWebhookDeliveries = (id) =>
  api.get(`/api/crm/platform/webhooks/${id}/deliveries`);

/* Audit log */
export const listAuditLog = (params) =>
  api.get("/api/crm/org/audit", { params });

/* Version history — kind ∈ role | pipeline | workflow */
export const listVersions = (kind, entityId) =>
  api.get(`/api/crm/org/versions/${kind}/${entityId}`);

export default api;
